import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRight, Hash } from 'lucide-react';
import { useAuth } from '../lib/authContext';

/**
 * Nudges to use the one-time manual serial entry (individual orgs, 929).
 *
 * The pending count comes from the server (`GET /me` serialEntry* fields,
 * client_serial_entry_pending) - already restricted to individual orgs and to
 * assets that are still eligible - so nothing here re-derives eligibility. No
 * new notification type and no server-side dismissal: the banner's dismissal is
 * session-only (component state), so it returns on the next page load until the
 * serial is entered.
 */
export interface SerialEntryNudgeSource {
  organizationType?: 'business' | 'individual' | 'broker' | null;
  serialEntryPendingCount?: number;
  serialEntryServerCount?: number;
  serialEntryFirstAssetId?: string | null;
}

export interface SerialEntryNudge {
  count: number;
  serverCount: number;
  firstAssetId: string;
  label: string;
}

/** Pure: null unless an individual org has a pending, linkable asset. */
export function serialEntryNudgeFrom(src: SerialEntryNudgeSource | null | undefined): SerialEntryNudge | null {
  const count = src?.serialEntryPendingCount ?? 0;
  const firstAssetId = src?.serialEntryFirstAssetId ?? null;
  if (src?.organizationType !== 'individual' || count <= 0 || !firstAssetId) return null;
  const serverCount = src.serialEntryServerCount ?? 0;
  return { count, serverCount, firstAssetId, label: serialEntryLabel(count, serverCount) };
}

export function useSerialEntryNudge(): SerialEntryNudge | null {
  return serialEntryNudgeFrom(useAuth());
}

/** "1 asset" / "4 assets" / "4 components across 1 server" - group counts for a server with many GPU children. */
export function serialEntryLabel(count: number, serverCount: number): string {
  if (serverCount > 0 && serverCount < count) {
    return `${count} components across ${serverCount} ${serverCount === 1 ? 'server' : 'servers'}`;
  }
  return `${count} ${count === 1 ? 'asset' : 'assets'}`;
}

/** Compact prompt for ProbeSessionSummary / AddAssetSuccessPanel. Renders nothing when there is nothing pending. */
export function SerialEntryPrompt() {
  const nudge = useSerialEntryNudge();
  if (!nudge) return null;
  return (
    <div className="mt-3 border-t border-brand-200 pt-3" data-testid="serial-entry-prompt">
      <p className="text-xs text-muted-foreground">
        {nudge.label} from this scan {nudge.count === 1 ? 'has' : 'have'} no serial number yet. You can add it once.
      </p>
      <Link
        to="/assets/$assetId"
        params={{ assetId: nudge.firstAssetId as string }}
        className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-brand-700 hover:underline"
      >
        Add a serial number <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

/** Dashboard banner (same look as the onboarding banner). Session-only dismissal. */
export function SerialEntryBanner({ source }: { source: SerialEntryNudgeSource | null | undefined }) {
  const nudge = serialEntryNudgeFrom(source);
  const [dismissed, setDismissed] = useState(false);
  if (!nudge || dismissed) return null;
  return (
    <div
      className="flex items-center gap-3 rounded-lg border border-brand-200 bg-brand-50 px-4 py-3"
      data-testid="serial-entry-banner"
    >
      <Hash className="size-5 shrink-0 text-brand-600" />
      <p className="flex-1 text-sm text-brand-700">
        {nudge.label} from TrueRead {nudge.count === 1 ? 'is' : 'are'} missing a serial number. Add it now - you can
        only enter it once.
      </p>
      <Link
        to="/assets/$assetId"
        params={{ assetId: nudge.firstAssetId as string }}
        className="shrink-0 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
      >
        Add serial
      </Link>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => setDismissed(true)}
        className="shrink-0 text-xs text-brand-700 hover:underline"
      >
        Later
      </button>
    </div>
  );
}
